import React from 'react';
import { useSpendingSummary, useOpenCustomerPortal } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function SpendingSummary({ showToast, dark = false }) {
  const { data: summary, isLoading: loading } = useSpendingSummary();
  const openPortalMutation = useOpenCustomerPortal();

  const handleOpenCustomerPortal = async () => {
    try {
      const returnUrl = `${window.location.origin}/dashboard`;
      const data = await openPortalMutation.mutateAsync(returnUrl);
      window.location.href = data.portalUrl;
    } catch (error) {
      captureError(error, {
        action: 'handleOpenCustomerPortal',
        component: 'SpendingSummary',
      });
      if (showToast) {
        showToast(error.message || 'Failed to open billing portal', 'error');
      } else {
        console.error('Failed to open billing portal:', error);
      }
    }
  };

  // Styling based on theme
  const containerClasses = dark 
    ? "bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-6 shadow-xl"
    : "bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6";
  const titleClasses = dark ? "text-white" : "text-gray-900";
  const textClasses = dark ? "text-gray-300" : "text-gray-600";
  const mutedTextClasses = dark ? "text-gray-400" : "text-gray-500";
  const statBoxClasses1 = dark 
    ? "bg-zapt-purple/30 backdrop-blur-sm border border-zapt-purple/50 rounded-xl p-4"
    : "bg-gradient-to-br from-zapt-purple/10 to-zapt-purple/5 rounded-lg p-4";
  const statBoxClasses2 = dark 
    ? "bg-zapt-yellow/20 backdrop-blur-sm border border-zapt-yellow/40 rounded-xl p-4"
    : "bg-gradient-to-br from-zapt-yellow/10 to-zapt-yellow/5 rounded-lg p-4";
  const statValueClasses = dark ? "text-zapt-yellow" : "text-zapt-purple";

  if (loading) {
    return (
      <div className={containerClasses}>
        <h2 className={`text-xl font-bold ${titleClasses} mb-4`}>💳 Billing & Spending</h2>
        <div className={`text-center py-4 ${mutedTextClasses}`}>Loading...</div>
      </div>
    );
  }

  if (!summary || ((summary.totalChargesAllTime || 0) === 0 && (summary.totalPaidInvoices || 0) === 0 && (summary.totalMonthlyFees || 0) === 0)) {
    return null; // Don't show if no spending
  }

  const totalChargesThisMonth = summary.totalChargesThisMonth || 0;
  const totalMonthlyFees = summary.totalMonthlyFees || 0;
  const appsWithFees = summary.appsWithFees || 0;

  return (
    <div className={containerClasses}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <h2 className={`text-xl font-bold ${titleClasses}`}>💳 Billing & Spending</h2>
        <button
          onClick={handleOpenCustomerPortal}
          className="w-full sm:w-auto px-4 py-2 bg-zapt-yellow text-zapt-navy rounded-lg font-semibold hover:bg-zapt-yellow/90 transition-colors text-sm shadow-lg shadow-zapt-yellow/25"
        >
          View Stripe Portal →
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Build Charges - This Month */}
        <div className={statBoxClasses1}>
          <p className={`text-sm ${textClasses} mb-1`}>Build Charges This Month</p>
          <p className={`text-2xl font-bold ${statValueClasses}`}>
            ${totalChargesThisMonth.toFixed(2)}
          </p>
        </div>

        {/* Monthly Maintenance - Projected */}
        <div className={statBoxClasses2}>
          <p className={`text-sm ${textClasses} mb-1`}>Expected Monthly Fees</p>
          <p className={`text-2xl font-bold ${statValueClasses}`}>
            ${totalMonthlyFees.toFixed(2)}/mo
          </p>
          <p className={`text-xs ${mutedTextClasses} mt-1`}>
            {appsWithFees} app{appsWithFees !== 1 ? 's' : ''} with maintenance fees
          </p>
        </div>
      </div>

      <p className={`text-xs ${mutedTextClasses} text-center`}>
        View complete invoice history, payment methods, and billing details in Stripe Customer Portal
      </p>
    </div>
  );
}

